import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";

import ScopePicker from "../configurations/company_team_modals/ScopePicker";
import { formatFlagName } from "./featureFlagConstants";

// Beyond this many flags the chip list stops being scannable and turns into a
// wall (a paste carries the whole flag set), so we summarize instead.
const MAX_FLAG_CHIPS = 6;

/**
 * Asks where a just-written company default should be pushed. The default is
 * already saved by the time this opens — the only decision here is whether to
 * also overwrite existing locations, and which ones.
 *
 * `locations` is the caller's DISPLAYED list, so "All locations" can never
 * touch company stores the admin can't see.
 */
export default function ApplyDefaultsToLocationsModal({
  open,
  onClose,
  onApply,
  flags,
  locations,
  title,
  description,
  summary,
  dismissLabel = "Just the default",
}) {
  const [scope, setScope] = React.useState("all");
  const [storeIds, setStoreIds] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setScope("all");
      setStoreIds([]);
      setSubmitting(false);
    }
  }, [open]);

  const flagEntries = Object.entries(flags || {});
  const allLocations = locations || [];
  const total = allLocations.length;

  const targetIds =
    scope === "all"
      ? allLocations.map((location) => location.id).filter(Boolean)
      : storeIds;

  // A config push has no flag list to enumerate; `summary` names the surface
  // instead, and there is always something to send.
  const hasPayload = Boolean(summary) || flagEntries.length > 0;
  const canSubmit = !submitting && hasPayload && targetIds.length > 0;

  // On success the caller retires the flags it pushed, which closes this modal
  // by dropping the prompt. It deliberately does NOT close it here: a default
  // write can resolve and merge a new flag in mid-submit, and that flag still
  // needs its own apply decision.
  const handleApply = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    await onApply(targetIds);
    setSubmitting(false);
  };

  const scopeStores = allLocations.map((location) => ({
    id: location.id,
    store_name: location.store_name,
    status: location.onboarding_status,
  }));

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxHeight: "90vh",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {summary ? (
            <p className="text-sm text-slate-700">{summary}</p>
          ) : flagEntries.length <= MAX_FLAG_CHIPS ? (
            <div className="flex flex-wrap gap-1.5">
              {flagEntries.map(([flag, value]) => (
                <span
                  key={flag}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700"
                >
                  {formatFlagName(flag)}
                  <span
                    className={`font-semibold ${
                      value ? "text-violet-700" : "text-slate-400"
                    }`}
                  >
                    {value ? "On" : "Off"}
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-700">
              {flagEntries.length} settings will be applied.
            </p>
          )}

          {description && <p className="text-sm text-slate-500">{description}</p>}

          <ScopePicker
            scope={scope}
            setScope={setScope}
            storeIds={storeIds}
            setStoreIds={setStoreIds}
            companyStores={scopeStores}
            allLabel={`All ${total} location${total === 1 ? "" : "s"}`}
            specificLabel="Specific locations only"
            radioGroupName="apply-company-default-scope"
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-white disabled:opacity-50"
          >
            {dismissLabel}
          </button>
          <LoadingButton
            variant="contained"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleApply}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#6d28d9",
              "&:hover": { backgroundColor: "#5b21b6" },
              fontSize: 14,
              px: 2.5,
              py: 1,
            }}
          >
            {`Apply to ${targetIds.length} location${
              targetIds.length === 1 ? "" : "s"
            }`}
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}
